import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["as", "title", "key", "href", "active", "showIcon"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { cloneElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames/bind';
import { any, arrayOf, bool, elementType, func, node, number, object, objectOf, oneOf, oneOfType, shape, string } from 'prop-types';
import { useMediaQuery, useWindowSize } from "../hooks";
import { ArrowLeft, Burger, CloseLinear, Oneworld, Search, ShoppingBagFilled, UserLinear } from "../icons";
import { ClickOutside } from "../internal/ClickOutside";
import { TextBase } from "../internal/TextBase";
import { deprecate } from "../utils/deprecate";
import noop from "../utils/noop";
import { currencyCodes, CurrencySelect, defaultCurrenciesCodes } from '../CurrencySelect';
import { defaultLanguages, LanguageSelect } from '../LanguageSelect';
import Popover from '../Popover';
import { HeaderSearch, Menu, MobileMenu, NewWindowIcon } from './components';
import { RegionSelect } from './components/RegionSelect';
import { AVATARS, CURRENCY_STYLE, LOGOS, MOBILE_LOGOS, themes } from './constants';
import { useHeader } from './useHeader';
import styles from './Header.css';
var cx = classNames.bind(styles);

var getNewTabProps = function getNewTabProps(predicate) {
  return predicate ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {};
};

var deprecation = deprecate('Компонент Header устареет начиная с версии 4.0.0, будет заменен HeaderCanary.', 'Header');
var Header = /*#__PURE__*/forwardRef(function Header(_ref, ref) {
  var avatar = _ref.avatar,
      backLinkClose = _ref.backLinkClose,
      backLinkTitle = _ref.backLinkTitle,
      cartCount = _ref.cartCount,
      cartTitle = _ref.cartTitle,
      currency = _ref.currency,
      currencies = _ref.currencies,
      currencyTitle = _ref.currencyTitle,
      headerTitle = _ref.headerTitle,
      hideCurrency = _ref.hideCurrency,
      hideRegion = _ref.hideRegion,
      login = _ref.login,
      language = _ref.language,
      languageSelectProps = _ref.languageSelectProps,
      langs = _ref.languages,
      linkBack = _ref.linkBack,
      linkHome = _ref.linkHome,
      linkHomeInNewTab = _ref.linkHomeInNewTab,
      linkHomeProps = _ref.linkHomeProps,
      linkOneWorld = _ref.linkOneWorld,
      linkOneWorldInNewTab = _ref.linkOneWorldInNewTab,
      LoginForm = _ref.LoginForm,
      loginTitle = _ref.loginTitle,
      loginTitleProps = _ref.loginTitleProps,
      mobileMenuActive = _ref.mobileMenuActive,
      mobileMenuDetectActiveByPathname = _ref.mobileMenuDetectActiveByPathname,
      mobileMenuHint = _ref.mobileMenuHint,
      mobileMenuHome = _ref.mobileMenuHome,
      navigation = _ref.navigation,
      onChangeCurrency = _ref.onChangeCurrency,
      onChangeRegion = _ref.onChangeRegion,
      onChangeLanguage = _ref.onChangeLanguage,
      onChangeMobileMenuActiveLink = _ref.onChangeMobileMenuActiveLink,
      onClickBack = _ref.onClickBack,
      onClickCart = _ref.onClickCart,
      oneWorldLink = _ref.oneWorldLink,
      onLoginClick = _ref.onLoginClick,
      openMenuTitle = _ref.openMenuTitle,
      ProfileInfo = _ref.ProfileInfo,
      SearchProps = _ref.SearchProps,
      showSearch = _ref.showSearch,
      showMobileAddons = _ref.showMobileAddons,
      theme = _ref.theme,
      togglerProps = _ref.togglerProps,
      rightSide = _ref.rightSide,
      logo = _ref.logo,
      disableActiveElementUnderline = _ref.disableActiveElementUnderline,
      openUserInfo = _ref.openUserInfo,
      setOpenUserInfo = _ref.setOpenUserInfo,
      fixed = _ref.fixed;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isSearch = _useState2[0],
      setIsSearch = _useState2[1];

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      currentPopup = _useState4[0],
      setCurrentPopup = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isMobileMenu = _useState6[0],
      toggleMobileMenu = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      headerHeight = _useState8[0],
      setHeaderHeight = _useState8[1];

  var _useWindowSize = useWindowSize(),
      windowWidth = _useWindowSize.width;

  var breakpoints = useMediaQuery('xs', 'mdMax');
  var isMobile = breakpoints.xs;
  var isTablet = breakpoints.mdMax;
  deprecation();

  var _useHeader = useHeader({
    openUserInfo: openUserInfo,
    setOpenUserInfo: setOpenUserInfo
  }),
      isUserInfoOpen = _useHeader.isUserInfoOpen,
      setIsUserInfoOpen = _useHeader.setIsUserInfoOpen;

  var mobileMenuRef = useRef(null);
  var headerRef = useRef(ref);

  var handleToggleSearch = function handleToggleSearch() {
    return setIsSearch(!isSearch);
  };

  var closeSearch = useCallback(function () {
    return setIsSearch(false);
  }, []);
  var closeNavPopup = useCallback(function (e) {
    if (e.target.type === 'button') {
      return;
    }

    setCurrentPopup(null);
  }, []);
  var isShopTheme = useMemo(function () {
    return theme === 'shop';
  }, [theme]);
  useEffect(function () {
    if (!isTablet) {
      handleMenuClose();
    }
  }, [isTablet]);
  var mobileNavigation = useMemo(function () {
    if (!navigation) return undefined;
    return navigation.map(function (section) {
      if (!section.items) {
        return section;
      }

      var items = [];
      section.items.forEach(function (item) {
        if (item.points && item.points.length === 1 && item.points[0].type === 'Text') {
          return;
        }

        if (Array.isArray(item)) {
          items.push.apply(items, _toConsumableArray(item));
          return;
        }

        items.push(item);
      });
      return _objectSpread(_objectSpread({}, section), {}, {
        items: items
      });
    });
  }, [navigation]);

  var handleMenuOpen = function handleMenuOpen() {
    document.documentElement.classList.add(cx('html_ds_header_mobile_menu'));
    toggleMobileMenu(true);
  };

  var handleMenuClose = function handleMenuClose() {
    document.documentElement.classList.remove(cx('html_ds_header_mobile_menu'));
    toggleMobileMenu(false);
  };

  var LinkBack = useMemo(function () {
    return /*#__PURE__*/React.createElement("a", {
      className: cx('button'),
      href: linkBack,
      title: backLinkTitle
    }, /*#__PURE__*/React.createElement(ArrowLeft, null));
  }, [linkBack, backLinkTitle]);
  var BackArrow = useMemo(function () {
    if (backLinkClose && !isMobile) {
      return null;
    }

    if (linkBack) {
      return LinkBack;
    }

    if (onClickBack) {
      return /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: cx('button'),
        onClick: onClickBack,
        title: backLinkTitle
      }, backLinkClose ? /*#__PURE__*/React.createElement(CloseLinear, null) : /*#__PURE__*/React.createElement(ArrowLeft, null));
    }

    return null;
  }, [backLinkClose, isMobile, linkBack, onClickBack, LinkBack, backLinkTitle]);
  var CartButton = useMemo(function () {
    if (!onClickCart) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section', 'section_cart')
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: cx('cart_button', {
        cart_button_empty: !cartCount
      }),
      onClick: onClickCart,
      title: cartTitle
    }, /*#__PURE__*/React.createElement(ShoppingBagFilled, null), cartCount && /*#__PURE__*/React.createElement("span", {
      className: cx('pointer')
    }, cartCount)));
  }, [onClickCart, cartCount, cartTitle]);
  var Avatar = avatar || AVATARS[theme] || AVATARS.b2c;
  var Logo = LOGOS[theme];
  var LogoMobile = MOBILE_LOGOS[theme];
  var togglerContent;

  if (login) {
    togglerContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: cx('login_icon')
    }, /*#__PURE__*/React.createElement(UserLinear, null)), /*#__PURE__*/React.createElement(TextBase, _extends({
      weight: "semibold",
      color: "N300"
    }, loginTitleProps), loginTitle));
  } else {
    togglerContent = /*#__PURE__*/React.createElement(Avatar, {
      height: 32,
      width: 32
    });
  }

  var toggler = /*#__PURE__*/React.createElement("div", _extends({
    className: cx('login')
  }, togglerProps), togglerContent);
  var languageBase = /*#__PURE__*/React.createElement(LanguageSelect, _extends({
    align: "right",
    language: language,
    onChange: onChangeLanguage,
    languages: langs
  }, languageSelectProps));
  var regionBase = /*#__PURE__*/React.createElement(RegionSelect, {
    align: !isTablet ? 'right' : undefined,
    language: language,
    currency: currency,
    currencies: currencies,
    languages: langs,
    onChange: onChangeRegion
  });

  var Language = function Language() {
    if (!language) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section')
    }, languageBase);
  };

  var Currency = function Currency() {
    if (!currency || hideCurrency) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section')
    }, /*#__PURE__*/React.createElement(CurrencySelect, {
      align: "right",
      currency: currency,
      currencies: currencies,
      title: currencyTitle,
      onChange: onChangeCurrency,
      style: CURRENCY_STYLE
    }));
  };

  var Region = function Region() {
    if (hideRegion) {
      return null;
    }

    if (isShopTheme) {
      return /*#__PURE__*/React.createElement("div", {
        className: cx('section')
      }, regionBase);
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Currency, null), /*#__PURE__*/React.createElement(Language, null));
  };

  useEffect(function () {
    if (headerRef.current && fixed) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, [fixed, windowWidth]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("nav", {
    ref: headerRef,
    className: cx('root', "theme_".concat(theme), {
      fixed: fixed
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('top')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('wrapper')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('left_side')
  }, BackArrow, (navigation || showSearch) && !BackArrow && /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: handleMenuOpen,
    className: cx('button', 'tablet', 'mobile'),
    title: openMenuTitle
  }, isMobileMenu ? /*#__PURE__*/React.createElement(CloseLinear, null) : /*#__PURE__*/React.createElement(Burger, {
    size: "md"
  })), /*#__PURE__*/React.createElement("a", _extends({
    href: linkHome,
    className: cx('home_link'),
    title: headerTitle
  }, getNewTabProps(linkHomeInNewTab), linkHomeProps), /*#__PURE__*/React.createElement("span", {
    className: cx('logo_wrapper')
  }, ' ', logo || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: cx('logo', 'desktop', 'tablet')
  }, /*#__PURE__*/React.createElement(Logo, {
    height: "100%"
  })), /*#__PURE__*/React.createElement("span", {
    className: cx('logo', 'mobile')
  }, /*#__PURE__*/React.createElement(LogoMobile, {
    height: "100%"
  }))))), !isSearch && navigation && /*#__PURE__*/React.createElement("ul", {
    className: cx('navigation', 'desktop')
  }, navigation.map(function (_ref2, index) {
    var Component = _ref2.as,
        title = _ref2.title,
        key = _ref2.key,
        href = _ref2.href,
        active = _ref2.active,
        showIcon = _ref2.showIcon,
        rest = _objectWithoutProperties(_ref2, _excluded);

    var type = href ? 'Link' : 'Button';
    var renderByType = {
      Link: /*#__PURE__*/React.createElement("a", _extends({
        href: href,
        className: cx('navigation_point', {
          navigation_point_active: active && !disableActiveElementUnderline
        })
      }, rest), title, showIcon && /*#__PURE__*/React.createElement(NewWindowIcon, null)),
      Button: /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: cx('navigation_point', {
          navigation_point_active: active && !disableActiveElementUnderline
        }),
        onClick: function onClick() {
          setCurrentPopup(currentPopup === index ? null : index);
        }
      }, title)
    };
    return /*#__PURE__*/React.createElement("li", {
      key: key || "navItem-".concat(index),
      className: cx('navigation_item')
    }, Component ? /*#__PURE__*/React.createElement("span", {
      className: cx('navigation_point', {
        navigation_point_active: active && !disableActiveElementUnderline
      })
    }, /*#__PURE__*/React.createElement(Component, _extends({
      title: title,
      href: href
    }, rest))) : renderByType[type]);
  })), isSearch && /*#__PURE__*/React.createElement("div", {
    className: cx('search_wrapper')
  }, /*#__PURE__*/React.createElement(ClickOutside, {
    onClickOutside: closeSearch
  }, /*#__PURE__*/React.createElement(HeaderSearch, _extends({
    onClose: closeSearch
  }, SearchProps))))), /*#__PURE__*/React.createElement("div", {
    className: cx('right_side')
  }, rightSide || /*#__PURE__*/React.createElement(React.Fragment, null, showSearch && /*#__PURE__*/React.createElement("div", {
    className: cx('section', 'desktop')
  }, /*#__PURE__*/React.createElement("button", {
    onClick: handleToggleSearch,
    className: cx('search_button'),
    type: "button"
  }, /*#__PURE__*/React.createElement(Search, null))), CartButton, /*#__PURE__*/React.createElement("div", {
    className: cx('desktop', 'tablet', 'region_wrap', {
      mobile: theme === 'b2b' || showMobileAddons
    })
  }, /*#__PURE__*/React.createElement(Region, null)), login && !LoginForm && onLoginClick && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: cx('user_info'),
    onClick: onLoginClick
  }, toggler), (LoginForm || !login && ProfileInfo) && /*#__PURE__*/React.createElement("div", {
    className: cx('user_info')
  }, /*#__PURE__*/React.createElement(Popover, {
    isOpen: isUserInfoOpen,
    toggler: toggler,
    onToggle: setIsUserInfoOpen,
    align: "right",
    isMobileFixed: true
  }, function (_ref3) {
    var height = _ref3.height;
    return /*#__PURE__*/cloneElement(login ? LoginForm : ProfileInfo, {
      onClose: function onClose() {
        return setIsUserInfoOpen(false);
      },
      style: {
        height: height
      }
    });
  })), oneWorldLink && /*#__PURE__*/React.createElement("a", _extends({
    href: linkOneWorld,
    className: cx('oneworld_link')
  }, getNewTabProps(linkOneWorldInNewTab)), "OneWorld", /*#__PURE__*/React.createElement(Oneworld, null)))))), currentPopup !== null && /*#__PURE__*/React.createElement(ClickOutside, {
    onClickOutside: closeNavPopup
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('navigation_content')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('wrapper', 'wrapper_popup')
  }, navigation && /*#__PURE__*/React.createElement(Menu, {
    items: navigation[currentPopup].items,
    isPromo: navigation[currentPopup].isPromo,
    theme: theme
  })))), /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: cx('mobile_menu'),
    in: isTablet && isMobileMenu,
    nodeRef: mobileMenuRef,
    timeout: 500,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: mobileMenuRef,
    className: cx('mobile_menu')
  }, /*#__PURE__*/React.createElement(MobileMenu, {
    active: mobileMenuActive,
    byPathname: mobileMenuDetectActiveByPathname,
    hint: mobileMenuHint,
    home: mobileMenuHome,
    navigation: mobileNavigation,
    onChange: onChangeMobileMenuActiveLink,
    onClose: handleMenuClose,
    SearchProps: SearchProps,
    showSearch: showSearch,
    theme: theme,
    Region: Region,
    language: languageBase
  })))), fixed && /*#__PURE__*/React.createElement("div", {
    style: {
      height: headerHeight
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: cx('layer', {
      layer_visible: currentPopup !== null
    })
  }));
});
var navigationItemShape = process.env.NODE_ENV !== "production" ? shape({
  title: string,
  key: string,
  points: arrayOf(shape({
    title: string,
    key: string,
    href: string,
    as: elementType
  }))
}) : {};
var navigationShape = process.env.NODE_ENV !== "production" ? shape({
  as: elementType,
  title: string,
  key: string,
  href: string,
  active: bool,
  isPromo: bool,
  items: arrayOf(oneOfType([navigationItemShape, arrayOf(navigationItemShape)]))
}) : {};
Header.propTypes = process.env.NODE_ENV !== "production" ? {
  fixed: bool,
  avatar: elementType,
  backLinkClose: bool,
  backLinkTitle: string,
  cartCount: number,
  cartTitle: string,
  currency: string,
  currencies: arrayOf(oneOf(currencyCodes)),
  currencyTitle: string,
  headerTitle: string,
  hideRegion: bool,
  language: string,
  languages: arrayOf(oneOf(defaultLanguages)),
  languageSelectProps: object,
  linkBack: string,
  linkHome: string,
  linkHomeInNewTab: bool,
  linkHomeProps: object,
  linkOneWorld: string,
  linkOneWorldInNewTab: bool,
  login: bool,
  LoginForm: node,
  loginTitle: node,
  loginTitleProps: object,
  mobileMenuActive: oneOfType([number, object, string]),
  mobileMenuDetectActiveByPathname: bool,
  mobileMenuHint: shape({
    href: string,
    link: string,
    text: string,
    title: string
  }),
  mobileMenuHome: shape({
    href: string,
    title: string
  }),
  navigation: arrayOf(navigationShape),
  onChangeCurrency: func,
  onChangeRegion: func,
  onChangeLanguage: func,
  onChangeMobileMenuActiveLink: func,
  onClickCart: func,
  onClickBack: func,
  onClickMenu: func,
  oneWorldLink: bool,
  onLoginClick: func,
  openMenuTitle: string,
  ProfileInfo: node,
  SearchProps: shape(HeaderSearch.propTypes),
  showSearch: bool,
  showMobileAddons: bool,
  theme: oneOf(Object.values(themes)),
  disableActiveElementUnderline: bool,
  togglerProps: objectOf(any),
  rightSide: node,
  logo: node,
  hideCurrency: bool,
  openUserInfo: bool,
  setOpenUserInfo: func
} : {};
Header.defaultProps = {
  backLinkTitle: 'Назад',
  currencies: defaultCurrenciesCodes,
  headerTitle: 'S7 Airlines | Купить авиабилеты онлайн',
  languageSelectProps: {},
  linkHomeProps: {},
  loginTitle: 'Войти',
  loginTitleProps: {},
  onChangeCurrency: noop,
  onChangeLanguage: noop,
  onChangeRegion: noop,
  onClickMenu: noop,
  openMenuTitle: 'Открыть меню',
  theme: themes.b2c,
  togglerProps: {}
};
export default Header;